import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface User {
  id: number;
  username: string;
  email: string;
  role: 'super_admin' | 'admin' | 'user';
}

interface Workflow {
  id: number;
  name: string;
  category: string;
  workflow_type: string;
  has_access: boolean;
}

interface Folder {
  id: number;
  name: string;
  hotkey: string | null;
  color: string;
  created_at: string;
}

interface PermissionManagerProps {
  user: User;
  onClose: () => void;
  onUpdate: () => void;
}

const PermissionManager: React.FC<PermissionManagerProps> = ({ user, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState<'workflows' | 'folders'>('workflows');
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Folder form state
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [folderName, setFolderName] = useState('');
  const [folderHotkey, setFolderHotkey] = useState('');
  const [folderColor, setFolderColor] = useState('#3B82F6');

  useEffect(() => {
    if (activeTab === 'workflows') {
      fetchWorkflows();
    } else {
      fetchFolders();
    }
  }, [activeTab]);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/admin/users/${user.id}/workflows`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkflows(response.data);
    } catch (error: any) {
      console.error('Failed to fetch workflows:', error);
      alert(error.response?.data?.error || 'Failed to fetch workflows');
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/admin/users/${user.id}/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolders(response.data);
    } catch (error: any) {
      console.error('Failed to fetch folders:', error);
      alert(error.response?.data?.error || 'Failed to fetch folders');
    } finally {
      setLoading(false);
    }
  };

  const handleWorkflowToggle = async (workflowId: number, currentAccess: boolean) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      if (currentAccess) {
        // Revoke permission
        await axios.delete(`${API_BASE}/admin/users/${user.id}/workflows/${workflowId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Grant permission
        await axios.post(`${API_BASE}/admin/users/${user.id}/workflows/${workflowId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // Update local state
      setWorkflows(prev => prev.map(w =>
        w.id === workflowId ? { ...w, has_access: !currentAccess } : w
      ));

      onUpdate();
    } catch (error: any) {
      console.error('Failed to toggle permission:', error);
      alert(error.response?.data?.error || 'Failed to update permission');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateFolder = () => {
    setEditingFolder(null);
    setFolderName('');
    setFolderHotkey('');
    setFolderColor('#3B82F6');
    setShowFolderForm(true);
  };

  const handleEditFolder = (folder: Folder) => {
    setEditingFolder(folder);
    setFolderName(folder.name);
    setFolderHotkey(folder.hotkey || '');
    setFolderColor(folder.color);
    setShowFolderForm(true);
  };

  const handleSaveFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!folderName.trim()) {
      alert('Folder name is required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const folderData = {
        name: folderName.trim(),
        hotkey: folderHotkey.trim() || null,
        color: folderColor
      };

      if (editingFolder) {
        // Update folder
        const response = await axios.put(
          `${API_BASE}/admin/users/${user.id}/folders/${editingFolder.id}`,
          folderData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFolders(prev => prev.map(f => f.id === editingFolder.id ? response.data : f));
      } else {
        // Create folder
        const response = await axios.post(
          `${API_BASE}/admin/users/${user.id}/folders`,
          folderData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFolders(prev => [response.data, ...prev]);
      }

      setShowFolderForm(false);
      onUpdate();
    } catch (error: any) {
      console.error('Failed to save folder:', error);
      alert(error.response?.data?.error || 'Failed to save folder');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFolder = async (folder: Folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"? Images in this folder will be unfoldered.`)) {
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_BASE}/admin/users/${user.id}/folders/${folder.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setFolders(prev => prev.filter(f => f.id !== folder.id));
      alert(response.data.message + ` (${response.data.images_unfoldered} images unfoldered)`);
      onUpdate();
    } catch (error: any) {
      console.error('Failed to delete folder:', error);
      alert(error.response?.data?.error || 'Failed to delete folder');
    } finally {
      setSaving(false);
    }
  };

  const accessedCount = workflows.filter(w => w.has_access).length;
  const totalWorkflows = workflows.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">Manage Permissions & Folders</h2>
              <p className="text-blue-100 mt-1">
                {user.username} ({user.email})
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold leading-none"
            >
              ×
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-4 border-b border-blue-400">
            <button
              onClick={() => setActiveTab('workflows')}
              className={`pb-2 px-1 font-medium transition-colors ${
                activeTab === 'workflows'
                  ? 'text-white border-b-2 border-white'
                  : 'text-blue-200 hover:text-white'
              }`}
            >
              Workflow Permissions ({accessedCount}/{totalWorkflows})
            </button>
            <button
              onClick={() => setActiveTab('folders')}
              className={`pb-2 px-1 font-medium transition-colors ${
                activeTab === 'folders'
                  ? 'text-white border-b-2 border-white'
                  : 'text-blue-200 hover:text-white'
              }`}
            >
              Folders ({folders.length})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : activeTab === 'workflows' ? (
            <div>
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Super admins have access to all workflows.
                  Regular users with no permissions see all workflows.
                  Once you grant any permission, they only see permitted workflows.
                </p>
              </div>

              {workflows.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No workflows found</p>
              ) : (
                <div className="space-y-2">
                  {workflows.map(workflow => (
                    <div
                      key={workflow.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{workflow.name}</div>
                        <div className="text-sm text-gray-500 mt-1">
                          {workflow.category} • {workflow.workflow_type === 'slug' ? 'EOK Slug' : 'JSON'}
                        </div>
                      </div>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={workflow.has_access}
                          onChange={() => handleWorkflowToggle(workflow.id, workflow.has_access)}
                          disabled={saving}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {workflow.has_access ? 'Has Access' : 'No Access'}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">User Folders</h3>
                <button
                  onClick={handleCreateFolder}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  + Create Folder
                </button>
              </div>

              {showFolderForm && (
                <form onSubmit={handleSaveFolder} className="mb-6 p-4 bg-gray-50 border rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-4">
                    {editingFolder ? 'Edit Folder' : 'Create New Folder'}
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Folder Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={folderName}
                        onChange={(e) => setFolderName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hotkey
                      </label>
                      <input
                        type="text"
                        maxLength={1}
                        value={folderHotkey}
                        onChange={(e) => setFolderHotkey(e.target.value.toUpperCase())}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="A-Z"
                      />
                    </div>
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Color
                      </label>
                      <input
                        type="color"
                        value={folderColor}
                        onChange={(e) => setFolderColor(e.target.value)}
                        className="w-full h-10 px-1 py-1 border border-gray-300 rounded-lg cursor-pointer"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : editingFolder ? 'Update Folder' : 'Create Folder'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowFolderForm(false)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {folders.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No folders yet. Create one to get started.</p>
              ) : (
                <div className="space-y-2">
                  {folders.map(folder => (
                    <div
                      key={folder.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div
                          className="w-8 h-8 rounded"
                          style={{ backgroundColor: folder.color }}
                        />
                        <div>
                          <div className="font-medium text-gray-900">{folder.name}</div>
                          <div className="text-sm text-gray-500">
                            {folder.hotkey ? `Hotkey: ${folder.hotkey}` : 'No hotkey'} •
                            Created {new Date(folder.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditFolder(folder)}
                          className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteFolder(folder)}
                          disabled={saving}
                          className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PermissionManager;
